import { isPinnedRowNode } from "@entrilia/shared/components/grid/gridHelper.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { AnyObject } from "@entrilia/shared/types.ts";
import { getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import { Alert, Stack } from "@mui/material";
import type {
  GridAlignment,
  GridCellParams,
  GridColDef,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  GridRowModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getTabularSnapshotPage } from "../../../../../../../api/biApi.ts";
import type {
  CalculationType,
  ColumnHorizontalAlignment,
} from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import {
  type GroupingField,
  type GroupTotal,
  type ReportField,
  type SortField,
  type TabularDataCell,
  type TabularGeneralSettings,
  type TabularRow,
  type TabularSnapshot,
} from "../../../../../../../api/types/svTypes.ts";
import { isAggregationBased, isCustomColumn, isDimension } from "../../../utilities/fieldsHelper.ts";
import type { ResponseColumn } from "../../../utilities/gridStateHelper.ts";
import type { DataGridCustomProps } from "../../common/DataGridCustomProps.tsx";
import { hasAvailableDrillDown } from "../common/hasAvailableDrillDown.ts";
import { TabularGridCore } from "./TabularGridCore.tsx";
import { buildDimensionColumn } from "./utils/dimension-column/buildDimensionColumn.ts";
import { isTabularPageCancellation } from "./utils/isTabularPageCancellation.ts";
import { buildMeasureColumn, findGroupTotalCell } from "./utils/measure-column/buildMeasureColumn.ts";
import { getTabularColumnAlignments } from "./utils/tabularColumnAlignment.ts";
import {
  getTabularColumnWidthFields,
  getTabularColumnWidths,
  reconcileTabularColumnWidths,
  updateTabularColumnWidth,
} from "./utils/tabularColumnWidths.ts";
import { orderDisplayedColumns, updateTabularDisplayState } from "./utils/tabularDisplayState.ts";
import { createTabularSnapshotDataSource } from "./utils/tabularSnapshotDataSource.ts";
import { createTabularSnapshotRecovery } from "./utils/tabularSnapshotRecovery.ts";

const MemoizedTabularGridCore = memo(TabularGridCore);

interface Props extends DataGridCustomProps {
  snapshot?: TabularSnapshot;
  rows: TabularRow[];
  columns: ResponseColumn[] | undefined;
  groupTotals: GroupTotal[];
  grandTotals: AnyObject;
  groupingFields: GroupingField[];
  loading: boolean;
  onRefresh?: () => void;
  settings: TabularGeneralSettings;
  fields: ReportField[];
  sorts: SortField[];
  onGroupingChanged?: (model: GridRowGroupingModel) => void;
  onColumnsSortingChanged?: (gridSortModel: GridSortModel) => void;
  onColumnsOrderChanged?: (names: string[]) => void;
  onDrillDown: (cell: TabularDataCell | undefined, fieldGuid: string) => void;
  onFreezedColumnsChange?: (freezedColumns: GridPinnedColumnFields) => void;
  onColumnWidthChanged?: (field: string, width: number | undefined, applyToAll?: boolean) => void;
  onColumnAlignmentChanged?: (field: string, alignment: ColumnHorizontalAlignment | undefined) => void;
  onPredefinedCustomFieldType?: (
    calculationType: CalculationType,
    fieldGuid: string,
    anchorEl: HTMLElement | null
  ) => void;
}

interface ContentProps extends Omit<Props, "onRefresh"> {
  onResultExpired: () => boolean;
  columnWidths: ReadonlyMap<string, number>;
  customColumnWidths: ReadonlyMap<string, number>;
  columnAlignments: ReadonlyMap<string, GridAlignment>;
  onColumnWidthChange: (field: string, width: number | undefined, manual: boolean, applyToAll?: boolean) => void;
}

const TabularPreviewGrid = (props: Props) => {
  const [display, setDisplay] = useState(() => updateTabularDisplayState(undefined, props));
  const nextDisplay = updateTabularDisplayState(display, props);
  if (nextDisplay !== display) {
    setDisplay(nextDisplay);
  }
  const columns = useMemo(
    () => orderDisplayedColumns(nextDisplay.result.columns, props.columns),
    [nextDisplay.result.columns, props.columns]
  );
  const widthFields = useMemo(
    () =>
      getTabularColumnWidthFields(
        props.fields.map((field) => field.config),
        props.groupingFields
      ),
    [props.fields, props.groupingFields]
  );
  const savedWidths = useMemo(() => getTabularColumnWidths(widthFields), [widthFields]);
  const columnAlignments = useMemo(
    () =>
      getTabularColumnAlignments(
        props.fields.map((field) => field.config),
        props.groupingFields
      ),
    [props.fields, props.groupingFields]
  );
  const [widthState, setWidthState] = useState(() => reconcileTabularColumnWidths(undefined, savedWidths));
  const currentWidths = reconcileTabularColumnWidths(widthState, savedWidths);
  if (currentWidths !== widthState) {
    setWidthState(currentWidths);
  }
  const { onColumnWidthChanged, disableColumnManagement } = props;
  const handleColumnWidthChange = useCallback(
    (field: string, width: number | undefined, manual: boolean, applyToAll?: boolean) => {
      const pixels = width === undefined ? undefined : Math.round(width);
      setWidthState((state) =>
        applyToAll
          ? widthFields.reduce((updated, item) => updateTabularColumnWidth(updated, item.guid, pixels, manual), state)
          : updateTabularColumnWidth(state, field, pixels, manual)
      );
      if (manual && !disableColumnManagement) {
        onColumnWidthChanged?.(field, pixels, applyToAll);
      }
    },
    [onColumnWidthChanged, disableColumnManagement, widthFields]
  );
  const [recovery] = useState(createTabularSnapshotRecovery);
  const handleResultExpired = useCallback(
    () => recovery.tryRefresh(props.loading, props.onRefresh),
    [recovery, props.loading, props.onRefresh]
  );
  return (
    <TabularPreviewGridContent
      key={nextDisplay.revision}
      {...props}
      {...nextDisplay.result}
      columns={columns}
      onResultExpired={handleResultExpired}
      columnWidths={currentWidths.widths}
      customColumnWidths={currentWidths.customWidths}
      columnAlignments={columnAlignments}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
};

const TabularPreviewGridContent = ({
  rows,
  snapshot,
  columns,
  groupTotals,
  grandTotals,
  groupingFields,
  loading,
  size,
  onGroupingChanged,
  onColumnsSortingChanged,
  onColumnsOrderChanged,
  onDrillDown,
  onFreezedColumnsChange,
  onPredefinedCustomFieldType,
  settings,
  fields,
  sorts,
  disableColumnManagement,
  disableCellClick,
  columnWidths,
  customColumnWidths,
  columnAlignments,
  onColumnWidthChange,
  onColumnAlignmentChanged,
  onResultExpired,
}: ContentProps) => {
  const [pageError, setPageError] = useState<string>();
  const handlePageError = useCallback((error: Error) => {
    if (isTabularPageCancellation(error)) {
      return;
    }
    logError(error, "[TabularPreviewGrid] loadReportPage");
  }, []);
  const apiRef = useGridApiRef();
  const onResultExpiredRef = useRef(onResultExpired);
  useLayoutEffect(() => {
    onResultExpiredRef.current = onResultExpired;
  }, [onResultExpired]);
  const handleResultExpired = useCallback(() => onResultExpiredRef.current(), []);
  const snapshotSource = useMemo(
    () =>
      snapshot
        ? createTabularSnapshotDataSource(
            snapshot,
            rows,
            grandTotals,
            getTabularSnapshotPage,
            setPageError,
            handleResultExpired
          )
        : undefined,
    [snapshot, rows, grandTotals, handleResultExpired]
  );
  useEffect(() => {
    snapshotSource?.activate();
    return () => snapshotSource?.dispose();
  }, [snapshotSource]);
  const sortsFieldsConfiguration = useMemo(() => sorts.map((sav) => sav.config), [sorts]);
  const fieldsIds = useMemo(() => fields.map((f) => f.config.guid), [fields]);

  const fieldsRef = useRef(fields);
  const groupTotalsRef = useRef(groupTotals);
  const grandTotalsRef = useRef(grandTotals);
  const columnsRef = useRef(columns);
  useEffect(() => {
    fieldsRef.current = fields;
    groupTotalsRef.current = groupTotals;
    grandTotalsRef.current = grandTotals;
    columnsRef.current = columns;
  }, [columns, fields, grandTotals, groupTotals]);

  const getFieldTitle = useCallback((field: string) => {
    return columnsRef.current?.find((c) => c.id === field)?.title || "";
  }, []);

  const isColumnFailedToLoad = useCallback((fieldGuid: string): boolean => {
    return columnsRef.current?.find((c) => c.id === fieldGuid)?.failedToLoad || false;
  }, []);

  const [gridColumns, setGridColumns] = useState<GridColDef[]>([]);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setGridColumns(
      (columns ?? [])
        .map((column) => {
          const field = fieldsRef.current.find((f) => f.config.guid === column.id);
          if (!field) {
            return;
          }
          if (isDimension(field)) {
            return buildDimensionColumn(apiRef, field, getFieldTitle);
          }
          return buildMeasureColumn({
            apiRef,
            field,
            getFieldTitle,
            fields: fieldsRef,
            grandTotals: grandTotalsRef,
            groupTotals: groupTotalsRef,
            disableCellClick: !!disableCellClick,
            isFailedToLoad: isColumnFailedToLoad,
          });
        })
        .filter((c): c is GridColDef => !!c)
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [columns, apiRef, groupTotals, disableCellClick]);

  // MUI DataGrid can keep focus on a removed column while column definitions are rebuilding.
  // Clearing focus avoids crashes in internal getCellParams/getRowValue handlers.
  useLayoutEffect(() => {
    apiRef.current?.setState((gridState) => ({
      ...gridState,
      focus: { cell: null, columnHeader: null, columnHeaderFilter: null, columnGroupHeader: null },
    }));
  }, [columns, apiRef]);

  const handleCellClicked = React.useCallback(
    (params: GridCellParams<GridRowModel<TabularRow>>) => {
      const field = fieldsRef.current.find((f) => f.config.guid === params.field);
      if (!isAggregationBased(field) && !isCustomColumn(field)) {
        return;
      }
      //Grand total
      if (isPinnedRowNode(params.rowNode)) {
        const cell = grandTotalsRef.current[params.field] as TabularDataCell;
        if (hasAvailableDrillDown(cell?.drillDownId)) {
          onDrillDown(cell, params.field);
          return;
        }
      } else if (params.rowNode.type === "group" || params.rowNode.type === "footer") {
        // Group subtotal: navigate up to the owning group node and look up the group total cell
        const groupNode = params.rowNode.type === "footer" ? getRowNode(apiRef, params.rowNode.parent) : params.rowNode;
        if (groupNode?.type !== "group") {
          return;
        }
        const cell = findGroupTotalCell(
          groupNode,
          params.field,
          fieldsRef.current,
          grandTotalsRef.current,
          groupTotalsRef.current,
          apiRef
        );
        if (cell && hasAvailableDrillDown(cell.drillDownId)) {
          onDrillDown(cell, params.field);
        }
        return;
      }
      const cell = params.row.data?.[params.field] as TabularDataCell;
      if (hasAvailableDrillDown(cell?.drillDownId)) {
        onDrillDown(cell, params.field);
        return;
      }
    },
    [onDrillDown, apiRef]
  );

  const onColumnOrderChange = useCallback(() => {
    const newOrder = apiRef.current?.getAllColumns().map((c) => c.field) || [];
    onColumnsOrderChanged?.call(null, newOrder);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [onColumnsOrderChanged]);

  return (
    <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0, width: "100%" }}>
      {pageError && (
        <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>
          {pageError}
        </Alert>
      )}
      <MemoizedTabularGridCore
        columnWidths={columnWidths}
        customColumnWidths={customColumnWidths}
        columnAlignments={columnAlignments}
        onColumnWidthChange={onColumnWidthChange}
        onColumnAlignmentChange={onColumnAlignmentChanged}
        onDataSourceError={handlePageError}
        apiRef={apiRef}
        dataSource={snapshotSource?.dataSource}
        dataSourceCache={snapshotSource?.cache}
        maxWidth={size.width}
        rows={rows}
        columns={gridColumns}
        loading={loading}
        onColumnOrderChange={onColumnOrderChange}
        onRowGroupingModelChange={onGroupingChanged}
        onSortModelChange={onColumnsSortingChanged}
        onCellClick={handleCellClicked}
        showGroupedColumns={settings.showGroupedColumns}
        subtotalLabelPosition={settings.subtotalLabelPosition}
        subtotalLabelFormatPrefix={settings.subtotalLabelFormatPrefix}
        subtotalLabelFormatSuffix={settings.subtotalLabelFormatSuffix}
        onPinnedColumnsChange={onFreezedColumnsChange}
        freezedColumnIds={settings.freezedColumns}
        disableColumnManagement={!!disableColumnManagement}
        groupingFields={groupingFields}
        fieldsIds={fieldsIds}
        sortingFields={sortsFieldsConfiguration}
        groupSummaryPlace={settings.groupSummaryPlace}
        showGrandTotal={settings.showGrandTotal}
        onPredefinedCustomFieldType={onPredefinedCustomFieldType}
        isColumnFailedToLoad={isColumnFailedToLoad}
      />
    </Stack>
  );
};

export default TabularPreviewGrid;
